import type { AuthConfig } from '@cguard/auth';

interface VideoAuthConfigInput {
  issuer: string;
  clientId: string;
  redirectUri: string;
  transportAuthority?: string;
  browserOrigin?: string;
}

function normalizeHttpsUrl(value: string, fieldName: string): string {
  const parsed = new URL(value);
  const isLoopback =
    parsed.hostname === 'localhost' ||
    parsed.hostname === '127.0.0.1' ||
    parsed.hostname === '[::1]';
  if (parsed.protocol !== 'https:' && !isLoopback) {
    throw new Error(`${fieldName} must use HTTPS`);
  }
  return parsed.href.replace(/\/$/, '');
}

function keycloakMetadata(
  issuer: string,
  transportAuthority: string,
): NonNullable<AuthConfig['metadata']> {
  const protocolEndpoint = `${transportAuthority}/protocol/openid-connect`;
  return {
    issuer,
    authorization_endpoint: `${protocolEndpoint}/auth`,
    token_endpoint: `${protocolEndpoint}/token`,
    userinfo_endpoint: `${protocolEndpoint}/userinfo`,
    jwks_uri: `${protocolEndpoint}/certs`,
    end_session_endpoint: `${protocolEndpoint}/logout`,
    check_session_iframe: `${protocolEndpoint}/login-status-iframe.html`,
    revocation_endpoint: `${protocolEndpoint}/revoke`,
    introspection_endpoint: `${protocolEndpoint}/token/introspect`,
  };
}

function sameOriginTransportAuthority(issuer: string, browserOrigin: string): string {
  const issuerUrl = new URL(issuer);
  const originUrl = new URL(browserOrigin);
  return `${originUrl.origin}${issuerUrl.pathname}`;
}

export function createVideoAuthConfig(input: VideoAuthConfigInput): AuthConfig {
  const issuer = normalizeHttpsUrl(input.issuer, 'OIDC issuer');
  const config: AuthConfig = {
    authority: issuer,
    clientId: input.clientId,
    redirectUri: normalizeHttpsUrl(input.redirectUri, 'OIDC redirect URI'),
  };

  const transportAuthorityInput =
    input.transportAuthority ||
    (input.browserOrigin
      ? sameOriginTransportAuthority(
          issuer,
          normalizeHttpsUrl(input.browserOrigin, 'browser origin'),
        )
      : undefined);
  if (transportAuthorityInput) {
    const transportAuthority = normalizeHttpsUrl(
      transportAuthorityInput,
      'OIDC transport authority',
    );
    config.metadata = keycloakMetadata(issuer, transportAuthority);
  }

  return config;
}
